import cx from 'classnames';
import { useEffect, useRef, useState } from 'react';

import { IProfileBuilding } from '../../../frontend-libs/evlapp-types';
import { useNavigate } from 'react-router-dom';

import routes from '../../constants/routes';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { t } from '../../i18n';
import { changeBuilding, selectSecurity } from '../../store/securitySlice';

import { Button } from '../Button/Button';
import { Text } from '../Text/Text';
import { TipMessage } from '../TipMessage/TipMessage';

// Vyhledávání se ukáže až u delších seznamů — u 2–3 domů by jen překáželo
const SEARCH_FROM_COUNT = 6;

// Diakritiku ignorujeme na obou stranách ("trebic" najde Třebíč)
const normalizeForSearch = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

interface SidebarBuildingListProps {
  buildings: IProfileBuilding[];
  onHide: () => void;
}

export const SidebarBuildingList: React.FC<SidebarBuildingListProps> = (props: SidebarBuildingListProps) => {
  const { buildings, onHide } = props;
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const security = useAppSelector(selectSecurity);
  const ui = security.ui;

  // Modal se při zavření odmountuje (Modal vrací null), krok se tak resetuje sám
  const [step, setStep] = useState<'select' | 'add'>('select');
  const [search, setSearch] = useState('');
  const activeItemRef = useRef<HTMLDivElement>(null);

  // Registrace nového domu je správcovská věc — vlastníkům tlačítko nenabízíme
  const canAddBuilding = buildings.some((building) => building.isAdmin);
  const showSearch = buildings.length >= SEARCH_FROM_COUNT;

  useEffect(() => {
    activeItemRef.current?.scrollIntoView({ block: 'nearest' });
  }, []);

  const handleBuildingChange = (building: IProfileBuilding) => {
    dispatch(changeBuilding(building));
    navigate(routes.root);
    onHide();
  };

  const handleContinueToRegistration = () => {
    onHide();
    navigate(routes.registration);
  };

  if (step === 'add') {
    return (
      <div className="SidebarBuildingList">
        <Text size="h2" align="center" className="mb-2">
          {t('Sidebar.BuildingSelect.Add.title')}
        </Text>
        <Text size="body-medium" color="Neutral700" align="center" className="mb-3">
          {t('Sidebar.BuildingSelect.Add.text', undefined, { email: security.profile?.email })}
        </Text>
        <TipMessage title={t('Sidebar.BuildingSelect.Add.tipTitle')} className="mb-3">
          {t('Sidebar.BuildingSelect.Add.tipText')}
        </TipMessage>
        <Button wide title={t('Sidebar.BuildingSelect.Add.continue')} onClick={handleContinueToRegistration} />
        <Button
          wide
          variant="text"
          className="mt-2"
          title={t('Sidebar.BuildingSelect.Add.back')}
          onClick={() => setStep('select')}
        />
      </div>
    );
  }

  const normalizedSearch = normalizeForSearch(search.trim());
  const filteredBuildings = normalizedSearch
    ? buildings.filter((building) => normalizeForSearch(building.address).includes(normalizedSearch))
    : buildings;

  const list = filteredBuildings.map((building: IProfileBuilding) => {
    const isActive = building.buildingId === ui.buildingId;

    return (
      <div
        key={building.buildingId}
        ref={isActive ? activeItemRef : undefined}
        className={cx('SidebarUnit__building', isActive && 'SidebarUnit__building--active')}
      >
        <Text color="Inherit" onClick={() => handleBuildingChange(building)}>
          {building.address}
        </Text>
      </div>
    );
  });

  return (
    <div className="SidebarBuildingList">
      <Text size="h2" align="center" className="mb-2">
        {t('Sidebar.BuildingSelect.title')}
      </Text>
      <Text size="body-medium" color="Neutral700" align="center" className="mb-3">
        {t('Sidebar.BuildingSelect.subtitle')}
      </Text>
      {showSearch && (
        <input
          className="TextField__control SidebarBuildingList__search"
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('Sidebar.BuildingSelect.searchPlaceholder')}
          aria-label={t('Sidebar.BuildingSelect.searchPlaceholder')}
        />
      )}
      <div className="SidebarBuildingList__list">
        {list.length > 0 ? (
          list
        ) : (
          <Text size="body-medium" color="Neutral700" align="center" className="SidebarBuildingList__empty">
            {t('Sidebar.BuildingSelect.searchEmpty')}
          </Text>
        )}
      </div>
      {canAddBuilding && (
        <div className="SidebarBuildingList__footer">
          <Button
            variant="secondary"
            wide
            iconBefore="plus"
            title={t('Sidebar.BuildingSelect.addBuilding')}
            disabledDeps={['demo']}
            onClick={() => setStep('add')}
          />
        </div>
      )}
    </div>
  );
};
